/* eslint-disable */
import React, { forwardRef, useEffect, useMemo, useState } from 'react';
import { useTable, useRowSelect } from 'react-table';
import { toast } from 'react-toastify';
import { Button, CardBody, Modal, ModalBody, ModalFooter, ModalHeader, Table } from 'reactstrap';
import { invoiceService } from '../../services/InvoiceService';
import { InvoicingDto } from '../../types';

interface InvoiceEmailsTableProps {
    invoices: InvoicingDto[];
    fiscalYear: string;
    total: number;
    onSave: () => void;
    onClear: () => void;
}

interface Props {
    indeterminate?: boolean;
    name: string;
}

const useCombinedRefs = (...refs): React.MutableRefObject<any> => {
    const targetRef = React.useRef();

    React.useEffect(() => {
        refs.forEach((ref) => {
            if (!ref) return;

            if (typeof ref === 'function') {
                ref(targetRef.current);
            } else {
                ref.current = targetRef.current;
            }
        });
    }, [refs]);

    return targetRef;
};

const IndeterminateCheckbox = forwardRef<HTMLInputElement, Props>(
    ({ indeterminate, ...rest }, ref: React.Ref<HTMLInputElement>) => {
        const defaultRef = React.useRef(null);
        const combinedRef = useCombinedRefs(ref, defaultRef);

        useEffect(() => {
            if (combinedRef?.current) {
                combinedRef.current.indeterminate = indeterminate ?? false;
            }
        }, [combinedRef, indeterminate]);

        return (
            <React.Fragment>
                <input type="checkbox" ref={combinedRef} {...rest} />
            </React.Fragment>
        );
    },
);

const InvoiceEmailsTable = (props: InvoiceEmailsTableProps) => {
    const [showConfirmModal, setShowConfirmModal] = useState(false);

    const columns = useMemo(
        () => [
            { Header: '', accessor: 'id' },
            { Header: 'Invoice Number', accessor: 'invoiceNumber' },
            { Header: 'Institution', accessor: 'institutionTitle' },
        ],
        [],
    );

    const {
        getTableProps,
        getTableBodyProps,
        headerGroups,
        rows,
        prepareRow,
        selectedFlatRows,
        state: { selectedRowIds },
    } = useTable(
        {
            columns,
            data: useMemo(() => [...props.invoices], [props.invoices]),
        },
        useRowSelect,
        (hooks) => {
            hooks.visibleColumns.push((columns) => [
                // Let's make a column for selection
                {
                    id: 'selection',
                    // The header can use the table's getToggleAllRowsSelectedProps method
                    // to render a checkbox
                    Header: ({ getToggleAllRowsSelectedProps }) => (
                        <div>
                            <IndeterminateCheckbox {...getToggleAllRowsSelectedProps()} />
                        </div>
                    ),
                    // The cell can use the individual row's getToggleRowSelectedProps method
                    // to the render a checkbox
                    Cell: ({ row }) => (
                        <div>
                            <IndeterminateCheckbox {...row.getToggleRowSelectedProps()} />
                        </div>
                    ),
                },
                ...columns,
            ]);
        },
    );

    const sendEmails = () => {
        const toastId = toast.info('Sending invoices...', { autoClose: false });

        invoiceService
            .emailInvoices(selectedFlatRows.map((x) => x.values.id))
            .then((response: any) => {
                toast.dismiss(toastId);
                if (response?.background) {
                    toast.success(
                        `${response.count} invoices are being sent in the background. You will receive an email when complete.`,
                        { autoClose: 10000, closeOnClick: true, draggable: true },
                    );
                } else {
                    toast.success('Invoices sent', { autoClose: 5000, closeOnClick: true, draggable: true });
                }
                props.onSave();
            })
            .catch((err: any) => {
                toast.dismiss(toastId);
                const message = err?.title || err?.message || err?.Message || (typeof err === 'string' ? err : 'Error sending invoices');
                toast.error(message, { autoClose: false, closeOnClick: true, draggable: true });
            });
    };

    const confirmSendAllUnsent = () => {
        setShowConfirmModal(false);
        const toastId = toast.info('Sending all unsent invoices...', { autoClose: false });

        invoiceService
            .emailAllUnsentInvoices(props.fiscalYear)
            .then((response) => {
                toast.dismiss(toastId);
                if (response.count === 0) {
                    toast.info('No unsent invoices found for this fiscal year.', { autoClose: 5000, closeOnClick: true, draggable: true });
                } else {
                    toast.success(
                        `${response.count} invoices are being sent in the background. You will receive an email when complete.`,
                        { autoClose: 10000, closeOnClick: true, draggable: true },
                    );
                }
                props.onClear();
            })
            .catch((err: any) => {
                toast.dismiss(toastId);
                const message = err?.title || err?.message || err?.Message || (typeof err === 'string' ? err : 'Error sending invoices');
                toast.error(message, { autoClose: false, closeOnClick: true, draggable: true });
            });
    };

    return (
        <>
            <CardBody className="mb-3 d-flex" style={{ gap: '0.5rem' }}>
                <Button color="primary" onClick={() => sendEmails()}>
                    Send Selected Invoices
                </Button>
                <Button color="warning" onClick={() => setShowConfirmModal(true)}>
                    Send All Unsent Invoices
                </Button>
            </CardBody>
            <Modal isOpen={showConfirmModal} toggle={() => setShowConfirmModal(false)} centered>
                <ModalHeader toggle={() => setShowConfirmModal(false)}>
                    Send All Unsent Invoices
                </ModalHeader>
                <ModalBody>
                    Send all {props.total} unsent invoices for fiscal year {props.fiscalYear}? Emails will be
                    processed in the background and you will receive a notification email when complete.
                </ModalBody>
                <ModalFooter>
                    <Button color="primary" className="mr-2" onClick={() => confirmSendAllUnsent()}>
                        Confirm
                    </Button>
                    <Button color="link" onClick={() => setShowConfirmModal(false)}>
                        Cancel
                    </Button>
                </ModalFooter>
            </Modal>
            <Table {...getTableProps()}>
                <thead>
                    {headerGroups.map((headerGroup) => (
                        <tr {...headerGroup.getHeaderGroupProps()}>
                            {headerGroup.headers.map((column) => (
                                <th {...column.getHeaderProps()}>{column.render('Header')}</th>
                            ))}
                        </tr>
                    ))}
                </thead>
                <tbody {...getTableBodyProps()}>
                    {rows.map((row, i) => {
                        prepareRow(row);
                        return (
                            <tr {...row.getRowProps()}>
                                {row.cells.map((cell) => {
                                    return <td {...cell.getCellProps()}>{cell.render('Cell')}</td>;
                                })}
                            </tr>
                        );
                    })}
                </tbody>
            </Table>
        </>
    );
};

export default InvoiceEmailsTable;
